// features/procurement/PurchaseOrderListPage.tsx
/**
 * Purchase Orders Management — Executive UI/UX Redesign
 * Featuring one-click approvals, direct PDF export, receiving shortcuts,
 * and zero-silent-fail error handling.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card } from '@/components/ui/Card';
import {
    FileText, Plus, Search, X, RefreshCw,
    CheckCircle, Clock, Send, Truck, Package, Ban, ArrowLeft,
    DollarSign, ChevronRight, Download, Printer, Store,
    Calendar, TrendingUp, ArrowUpRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getPurchaseOrders,
    approvePurchaseOrder,
    sendPurchaseOrder,
    downloadPoPdf,
    getProcurementErrorMessage,
    PurchaseOrder,
} from './services/procurementApi';

// ============================================================
// STATUS PILL COMPONENT
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    draft: { label: 'Draft', bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    approved: { label: 'Approved', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    sent: { label: 'Sent to Vendor', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
    partially_received: { label: 'Partially Received', bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
    fully_received: { label: 'Received', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    cancelled: { label: 'Cancelled', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
};

function StatusPill({ status }: { status: string }) {
    const config = STATUS_CONFIG[status] || {
        label: status.replace(/_/g, ' '),
        bg: 'bg-slate-100',
        text: 'text-slate-700',
        dot: 'bg-slate-400',
    };

    return (
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', config.bg, config.text)}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', config.dot)} />
            {config.label}
        </span>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function PurchaseOrderListPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: orders, isLoading, refetch } = useQuery<PurchaseOrder[]>({
        queryKey: ['purchase-orders-list', tenantId],
        queryFn: async () => {
            const result = await getPurchaseOrders(tenantId, { limit: 100 });
            return result.pos;
        },
        enabled: !!tenantId,
    });

    // ============================================================
    // MUTATIONS
    // ============================================================

    const approveMutation = useMutation({
        mutationFn: (poId: string) => approvePurchaseOrder(tenantId, poId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-orders'] });
            addToast({
                type: 'success',
                title: 'Purchase Order Approved',
                message: 'Order is authorized and ready for vendor dispatch.',
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Approval Failed',
                message: getProcurementErrorMessage(err, 'Failed to approve purchase order.'),
            });
        },
    });

    const sendMutation = useMutation({
        mutationFn: (poId: string) => sendPurchaseOrder(tenantId, poId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-orders'] });
            addToast({
                type: 'success',
                title: 'Order Sent to Vendor',
                message: 'PO marked as dispatched. Goods receipt can now be recorded.',
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Send Failed',
                message: getProcurementErrorMessage(err, 'Could not dispatch purchase order.'),
            });
        },
    });

    // ============================================================
    // COMPUTED STATS & FILTERING
    // ============================================================

    const ordersList = orders || [];

    const filteredOrders = useMemo(() => {
        return ordersList.filter((o) => {
            const matchesSearch =
                !search ||
                o.poNumber?.toLowerCase().includes(search.toLowerCase()) ||
                o.vendorName?.toLowerCase().includes(search.toLowerCase()) ||
                o.deliveryInstructions?.toLowerCase().includes(search.toLowerCase());

            const matchesStatus =
                statusFilter === 'all' || o.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [ordersList, search, statusFilter]);

    const activeCount = ordersList.filter((o) =>
        ['pending_approval', 'approved', 'sent', 'partially_received'].includes(o.status)
    ).length;

    const awaitingDeliveryCount = ordersList.filter((o) =>
        ['sent', 'partially_received'].includes(o.status)
    ).length;

    const totalSpend = ordersList
        .filter((o) => o.status !== 'cancelled')
        .reduce((sum, o) => sum + parseFloat(o.totalAmount || '0'), 0);

    const formatNaira = (val: number) =>
        `₦${val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const formatDate = (d: string) =>
        d ? new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            {/* Header */}
            <div className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1680px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/procurement')}
                                className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
                                type="button"
                                title="Back to Procurement"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl shadow-lg shadow-teal-500/20 text-white">
                                <FileText className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-slate-900 tracking-tight">Purchase Orders</h1>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {ordersList.length} total orders • {activeCount} active in pipeline
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2.5">
                            <button
                                onClick={() => refetch()}
                                className="p-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
                                title="Refresh Orders"
                                type="button"
                            >
                                <RefreshCw size={15} />
                            </button>

                            <button
                                onClick={() => navigate('/procurement/orders/new')}
                                className="px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition font-bold text-xs flex items-center gap-2 shadow-md shadow-teal-500/20"
                                type="button"
                            >
                                <Plus size={16} />
                                <span>Create Purchase Order</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1680px] mx-auto px-6 lg:px-8 pt-6 space-y-6">

                {/* Metric Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Orders</span>
                        <p className="text-2xl font-black text-slate-900 mt-1">{ordersList.length}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Order Volume</span>
                        <p className="text-2xl font-black text-slate-900 mt-1 truncate">{formatNaira(totalSpend)}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Awaiting Delivery</span>
                        <p className={clsx('text-2xl font-black mt-1', awaitingDeliveryCount > 0 ? 'text-cyan-600' : 'text-slate-900')}>
                            {awaitingDeliveryCount}
                        </p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Active Pipeline</span>
                        <p className="text-2xl font-black text-teal-700 mt-1">{activeCount}</p>
                    </div>
                </div>

                {/* Filter and Search Bar */}
                <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="relative w-full md:w-80">
                        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by PO #, supplier, instructions..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition"
                        />
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
                        {[
                            { id: 'all', label: 'All Orders' },
                            { id: 'pending_approval', label: 'Pending Approval' },
                            { id: 'approved', label: 'Approved' },
                            { id: 'sent', label: 'Sent' },
                            { id: 'partially_received', label: 'Partial' },
                            { id: 'fully_received', label: 'Received' },
                            { id: 'cancelled', label: 'Cancelled' },
                        ].map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => setStatusFilter(filter.id)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all',
                                    statusFilter === filter.id
                                        ? 'bg-teal-600 text-white shadow-sm'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                )}
                                type="button"
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table View */}
                {isLoading ? (
                    <div className="py-20 text-center flex flex-col items-center justify-center">
                        <Spinner size="lg" />
                        <p className="text-sm font-medium text-slate-500 mt-4">Loading purchase orders...</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mx-auto mb-4">
                            <FileText size={28} />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">No Purchase Orders Found</h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                            {search ? `No purchase orders matching "${search}".` : 'Issue formal purchase orders to your verified suppliers.'}
                        </p>
                        <button
                            onClick={() => navigate('/procurement/orders/new')}
                            className="mt-5 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            type="button"
                        >
                            + Create Purchase Order
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/70">
                                        <th className="py-3.5 px-6">PO Number</th>
                                        <th className="py-3.5 px-6">Supplier</th>
                                        <th className="py-3.5 px-6">Date</th>
                                        <th className="py-3.5 px-6">Status</th>
                                        <th className="py-3.5 px-6 text-right">Amount</th>
                                        <th className="py-3.5 px-6 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {filteredOrders.map((po) => (
                                        <tr
                                            key={po.id}
                                            onClick={() => navigate(`/procurement/orders/${po.id}`)}
                                            className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                                        >
                                            <td className="py-4 px-6 font-mono font-bold text-slate-900">
                                                {po.poNumber}
                                            </td>

                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                        {(po.vendorName || 'S').slice(0, 1).toUpperCase()}
                                                    </div>
                                                    <span className="font-semibold text-slate-800">
                                                        {po.vendorName || 'General Supplier'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="py-4 px-6 text-xs text-slate-500">
                                                {formatDate(po.createdAt)}
                                            </td>

                                            <td className="py-4 px-6">
                                                <StatusPill status={po.status || 'draft'} />
                                            </td>

                                            <td className="py-4 px-6 text-right font-black text-slate-900">
                                                {formatNaira(parseFloat(po.totalAmount || '0'))}
                                            </td>

                                            <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1.5">
                                                    {po.status === 'pending_approval' && (
                                                        <button
                                                            onClick={() => approveMutation.mutate(po.id)}
                                                            disabled={approveMutation.isPending}
                                                            className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 rounded-lg transition"
                                                            type="button"
                                                            title="Approve Order"
                                                        >
                                                            Approve
                                                        </button>
                                                    )}

                                                    {po.status === 'approved' && (
                                                        <button
                                                            onClick={() => sendMutation.mutate(po.id)}
                                                            disabled={sendMutation.isPending}
                                                            className="px-3 py-1 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 rounded-lg transition flex items-center gap-1"
                                                            type="button"
                                                            title="Send to Vendor"
                                                        >
                                                            <Send size={12} />
                                                            <span>Send</span>
                                                        </button>
                                                    )}

                                                    {(po.status === 'sent' || po.status === 'partially_received') && (
                                                        <button
                                                            onClick={() => navigate(`/procurement/grns/new?poId=${po.id}`)}
                                                            className="px-3 py-1 text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 hover:bg-cyan-100 rounded-lg transition flex items-center gap-1 shadow-sm"
                                                            type="button"
                                                            title="Record Goods Received"
                                                        >
                                                            <Truck size={12} />
                                                            <span>Receive</span>
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => downloadPoPdf(tenantId, po.id, po.poNumber)}
                                                        className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                                                        title="Export Official PDF"
                                                        type="button"
                                                    >
                                                        <Printer size={15} />
                                                    </button>

                                                    <button
                                                        onClick={() => navigate(`/procurement/orders/${po.id}`)}
                                                        className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                                                        type="button"
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}